import {
    Address,
    CheckoutParams,
    CheckoutSelectors,
    Consignment,
    Country,
    CustomerAddress,
    CustomerRequestOptions,
    FormField,
    RequestOptions,
    ShippingInitializeOptions,
    ShippingRequestOptions,
} from '@bigcommerce/checkout-sdk'
import { withFormik, FormikProps } from 'formik'
import { debounce, noop } from 'lodash'
import React, { PureComponent, ReactNode } from 'react'
import { lazy, object } from 'yup'
import { connect } from 'react-redux'
import B3Checkout, { PaymentsAdditionalTextField } from '../b3checkout'

// import { modifiedCustomFields } from '../b3checkout/utils/util'
import {
    getAddressFormFieldsValidationSchema,
    getTranslateAddressError,
    isEqualAddress,
    mapAddressFromFormValues,
    mapAddressToFormValues,
    AddressFormValues,
} from '../address'
import { getCustomFormFieldsValidationSchema } from '../formFields'
import { withLanguage, WithLanguageProps, TranslatedString } from '../locale'
import { Fieldset, Form, FormContext, Legend } from '../ui/form'

import hasSelectedShippingOptions from './hasSelectedShippingOptions'
import BillingSameAsShippingField from './BillingSameAsShippingField'
import ShippingAddress from './ShippingAddress'
import { SHIPPING_ADDRESS_FIELDS } from './ShippingAddressFields'
import ShippingFormFooter from './ShippingFormFooter'

export interface SingleShippingFormProps {
    addresses: CustomerAddress[];
    isBillingSameAsShipping: boolean;
    cartHasChanged: boolean;
    consignments: Consignment[];
    countries: Country[];
    countriesWithAutocomplete: string[];
    customerMessage: string;
    googleMapsApiKey?: string;
    isLoading: boolean;
    isShippingStepPending: boolean;
    isMultiShippingMode: boolean;
    methodId?: string;
    shippingAddress?: Address;
    shouldShowSaveAddress?: boolean;
    shouldShowOrderComments: boolean;
    deinitialize(options: ShippingRequestOptions): Promise<CheckoutSelectors>;
    deleteConsignments(): Promise<Address | undefined>;
    getFields(countryCode?: string): FormField[];
    initialize(options: ShippingInitializeOptions): Promise<CheckoutSelectors>;
    onSubmit(values: SingleShippingFormValues): void;
    onUnhandledError?(error: Error): void;
    signOut(options?: CustomerRequestOptions): void;
    updateAddress(address: Partial<Address>, options?: RequestOptions<CheckoutParams>): Promise<CheckoutSelectors>;
}

export interface SingleShippingFormValues {
    billingSameAsShipping: boolean
    shippingAddress?: AddressFormValues
    orderComment: string
    b2bCustomFields?: any
}

interface SingleShippingFormState {
    isResettingAddress: boolean
    isUpdatingShippingData: boolean
    hasRequestedShippingOptions: boolean
}

interface B3Props {
    b3Company: any
    isB3User: boolean
    checkoutSettings: any
}

export const SHIPPING_AUTOSAVE_DELAY = 1700

class SingleShippingForm extends PureComponent<
    B3Props &
    SingleShippingFormProps &
    WithLanguageProps &
    FormikProps<SingleShippingFormValues>
> {
    static contextType = FormContext

    state: SingleShippingFormState = {
        isResettingAddress: false,
        isUpdatingShippingData: false,
        hasRequestedShippingOptions: false,
    }

    private debouncedUpdateAddress: any

    constructor(
        props: B3Props &
            SingleShippingFormProps &
            WithLanguageProps &
            FormikProps<SingleShippingFormValues>
    ) {
        super(props)

        const { updateAddress } = this.props

        this.debouncedUpdateAddress = debounce(
            async (address: Address, includeShippingOptions: boolean) => {
                try {
                    await updateAddress(address, {
                        params: {
                            include: {
                                'consignments.availableShippingOptions': includeShippingOptions,
                            },
                        },
                    })
                    if (includeShippingOptions) {
                        this.setState({ hasRequestedShippingOptions: true })
                    }
                } finally {
                    this.setState({ isUpdatingShippingData: false })
                }
            },
            SHIPPING_AUTOSAVE_DELAY
        )
    }

    get shouldShowBillingSameAsShipping() {
        const {
            isB3User,
            addresses,
            methodId,
            shippingAddress,
        } = this.props

        const PAYMENT_METHOD_VALID = ['amazon', 'amazonpay']
        const shouldShowBillingSameAsShipping = !PAYMENT_METHOD_VALID.some(
            method => method === methodId
        )

        if (!shippingAddress) return false
        if (!isB3User) return shouldShowBillingSameAsShipping

        const comparedKeys = Object.keys(shippingAddress)
        let comparedAddresses = addresses.map(address =>
            comparedKeys.reduce((result, key) => {
                ; (result as any)[key] = (address as any)[key]
                return result
            }, {})
        )

        const index = comparedAddresses.findIndex((customerAddress) => isEqualAddress(customerAddress as CustomerAddress, shippingAddress))
        const shouldB3ShowBillingSameAsShipping = ~index ? (addresses[index] as CustomerAddress & { isBilling: number }).isBilling : true

        return !!(shouldShowBillingSameAsShipping && shouldB3ShowBillingSameAsShipping)
    }

    get shouldShowBillingSameAsShippingInCheckoutSettings() {
        const {
            checkoutSettings,
        } = this.props

        return checkoutSettings['shipping.singleAddress.allowAsBilling']
    }

    render(): ReactNode {
        const {
            addresses,
            cartHasChanged,
            isLoading,
            onUnhandledError,
            methodId,
            shouldShowSaveAddress,
            countries,
            countriesWithAutocomplete,
            googleMapsApiKey,
            shippingAddress,
            consignments,
            shouldShowOrderComments,
            initialize,
            isValid,
            deinitialize,
            values: { shippingAddress: addressForm },
            isShippingStepPending,
            isB3User,
            checkoutSettings,
        } = this.props

        const {
            isResettingAddress,
            isUpdatingShippingData,
            hasRequestedShippingOptions,
        } = this.state

        const getIsShowPaymentsAdditionalTextField = () => {
            const { checkoutPaymentAdditionalEnableExtra } = checkoutSettings
            return isB3User && +checkoutPaymentAdditionalEnableExtra.value === 1 && localStorage.getItem('IPCheckout') !== '1'
        }

        return (
            <Form autoComplete='on'>
                {getIsShowPaymentsAdditionalTextField() && <PaymentsAdditionalTextField checkoutSettings={checkoutSettings} />}

                <div className="form-legend-container">
                    <Legend testId="shipping-address-heading">
                        <TranslatedString id="shipping.shipping_address_heading" />
                    </Legend>
                </div>

                <Fieldset>
                    <ShippingAddress
                        addresses={addresses}
                        consignments={consignments}
                        countries={countries}
                        countriesWithAutocomplete={countriesWithAutocomplete}
                        deinitialize={deinitialize}
                        formFields={this.getFields(addressForm && addressForm.countryCode)}
                        googleMapsApiKey={googleMapsApiKey}
                        hasRequestedShippingOptions={hasRequestedShippingOptions}
                        initialize={initialize}
                        isLoading={isResettingAddress}
                        isShippingStepPending={isShippingStepPending}
                        methodId={methodId}
                        onAddressSelect={this.handleAddressSelect}
                        onFieldChange={this.handleFieldChange}
                        onUnhandledError={onUnhandledError}
                        onUseNewAddress={this.onUseNewAddress}
                        shippingAddress={shippingAddress}
                        shouldShowSaveAddress={shouldShowSaveAddress}
                    />
                    {false && this.shouldShowBillingSameAsShipping && this.shouldShowBillingSameAsShippingInCheckoutSettings && (
                        <div className='form-body'>
                            <BillingSameAsShippingField />
                        </div>
                    )}
                </Fieldset>

                <ShippingFormFooter
                    cartHasChanged={cartHasChanged}
                    isLoading={isLoading || isUpdatingShippingData}
                    isMultiShippingMode={false}
                    shouldDisableSubmit={this.shouldDisableSubmit()}
                    shouldShowOrderComments={shouldShowOrderComments}
                    shouldShowShippingOptions={isValid}
                />
            </Form>
        )
    }

    private shouldDisableSubmit: () => boolean = () => {
        const { isLoading, consignments, isValid } = this.props

        const { isUpdatingShippingData } = this.state

        if (!isValid) {
            return false
        }
        return (
            isLoading ||
            isUpdatingShippingData ||
            !hasSelectedShippingOptions(consignments)
        )
    }

    private handleFieldChange: (name: string) => void = async name => {
        const { setFieldValue } = this.props

        if (name === 'countryCode') {
            setFieldValue('shippingAddress.stateOrProvince', '')
            setFieldValue('shippingAddress.stateOrProvinceCode', '')
        }

        // Enqueue the following code to run after Formik has run validation
        await new Promise(resolve => setTimeout(resolve))

        const isShippingField = SHIPPING_ADDRESS_FIELDS.includes(name)

        const { hasRequestedShippingOptions } = this.state

        const { isValid } = this.props

        if (!isValid) {
            return
        }

        this.updateAddressWithFormData(
            isShippingField || !hasRequestedShippingOptions
        )
    }

    private updateAddressWithFormData(includeShippingOptions: boolean) {
        const {
            shippingAddress,
            values: { shippingAddress: addressForm },
        } = this.props

        const updatedShippingAddress =
            addressForm && mapAddressFromFormValues(addressForm)

        if (
            !updatedShippingAddress ||
            isEqualAddress(updatedShippingAddress, shippingAddress)
        ) {
            return
        }

        this.setState({ isUpdatingShippingData: true })
        this.debouncedUpdateAddress(updatedShippingAddress, includeShippingOptions)
    }

    private handleAddressSelect: (address: Address) => void = async address => {
        const {
            updateAddress,
            onUnhandledError = noop,
            values,
            setValues,
            isB3User,
        } = this.props

        this.setState({ isResettingAddress: true })


        let isEditAddress = B3Checkout.extrasFiles.setAddressExtrasFiles(isB3User, address, true)
        let getB2bCustomFields = isB3User ? B3Checkout.extrasFiles.setAddressSelectExtrasFiles(address) : {}
        if (isEditAddress) {
            setValues({
                ...values,
                shippingAddress: mapAddressToFormValues(
                    this.getFields(address.countryCode),
                    address
                ),
                b2bCustomFields: getB2bCustomFields
            })
            this.setState({ isResettingAddress: false })
            return
        }
        try {
            await updateAddress(address)

            setValues({
                ...values,
                shippingAddress: mapAddressToFormValues(
                    this.getFields(address.countryCode),
                    address
                ),
                b2bCustomFields: getB2bCustomFields
            })
        } catch (error) {
            onUnhandledError(error)
        } finally {
            this.setState({ isResettingAddress: false })
        }
    }

    private onUseNewAddress: () => void = async () => {
        const {
            deleteConsignments,
            onUnhandledError = noop,
            setValues,
            values,
            isB3User,
            // b3Company,
        } = this.props

        this.setState({ isResettingAddress: true })

        // let basicAddressInfo = b3Company?.basicAddressInfo

        if (isB3User) {
            // basicAddressInfo = modifiedCustomFields(b3Company?.basicAddressInfo)
            B3Checkout.extrasFiles.setDefaultAddress('shipping')
        }

        try {
            const address = await deleteConsignments()
            // setValues({
            //     ...values,
            //     shippingAddress: isB3User
            //         ? basicAddressInfo
            //         : mapAddressToFormValues(
            //             this.getFields(address && address.countryCode),
            //             address
            //         ),
            // })
            setValues({
                ...values,
                shippingAddress: mapAddressToFormValues(
                    this.getFields(address && address.countryCode),
                    address
                ),
            })
        } catch (e) {
            onUnhandledError(e)
        } finally {
            this.setState({ isResettingAddress: false })
        }
    }

    private getFields(countryCode: string | undefined): FormField[] {
        const { getFields } = this.props

        return getFields(countryCode)
    }
}

const mapStateToProps = (state: {
    global: {
        isB3User: boolean,
        checkoutSettings: any,
    }
    company: any
}) => {
    const {
        global: { isB3User, checkoutSettings },
        company: b3Company,
    } = state

    return {
        isB3User,
        b3Company,
        checkoutSettings,
    }
}

export default connect(mapStateToProps)(
    withLanguage(
        withFormik<
            B3Props & SingleShippingFormProps & WithLanguageProps,
            SingleShippingFormValues
        >({
            handleSubmit: (
                values,
                { props: { onSubmit, isB3User, getFields, addresses } }
            ) => {
                const { shippingAddress } = values
                const formFields = getFields(
                    shippingAddress && shippingAddress.countryCode
                )

                let comparedAddresses = [...addresses]
                if (shippingAddress?.stateOrProvinceCode) comparedAddresses = comparedAddresses.map(comparedAddress => ({
                    ...comparedAddress,
                    stateOrProvince: '',
                }))

                const isValid = B3Checkout.utils.util.validateAddress(mapAddressFromFormValues(shippingAddress as any), comparedAddresses, formFields)

                onSubmit({
                    ...values,
                    shippingAddress: {
                        ...values.shippingAddress,
                        addressId: isB3User && !isValid ? '' : undefined
                    } as unknown as AddressFormValues,
                })
            },
            mapPropsToValues: ({ getFields, shippingAddress, isBillingSameAsShipping, customerMessage }) => ({
                billingSameAsShipping: isBillingSameAsShipping,
                orderComment: customerMessage,
                shippingAddress: mapAddressToFormValues(
                    getFields(shippingAddress && shippingAddress.countryCode),
                    shippingAddress
                ),
                b2bCustomFields: B3Checkout.extrasFiles.setGoBackEcho('shipping')
            }),
            isInitialValid: ({ shippingAddress, getFields, language }) =>
                !!shippingAddress &&
                getAddressFormFieldsValidationSchema({
                    language,
                    formFields: getFields(shippingAddress.countryCode),
                }).isValidSync(shippingAddress),
            validationSchema: ({
                language,
                getFields,
                methodId,
            }: SingleShippingFormProps & WithLanguageProps) =>
                methodId
                    ? object({
                        shippingAddress: lazy<Partial<AddressFormValues>>(formValues =>
                            getCustomFormFieldsValidationSchema({
                                translate: getTranslateAddressError(language),
                                formFields: getFields(formValues && formValues.countryCode),
                            })
                        ),
                    })
                    : object({
                        shippingAddress: lazy<Partial<AddressFormValues>>(formValues =>
                            getAddressFormFieldsValidationSchema({
                                language,
                                formFields: getFields(formValues && formValues.countryCode),
                            })
                        ),
                        b2bCustomFields: lazy<Partial<any>>( () =>
                            B3Checkout.extrasFiles.getB3FormFieldsValidationSchema({
                                translate: B3Checkout.extrasFiles.getTranslateAddressError(language),
                                formFields: B3Checkout.extrasFiles.getB3ShippingAddressExtras(),
                            })
                        ),
                    }),
            enableReinitialize: false,
        })(SingleShippingForm)
    )
)
